import { useEffect, useState } from 'react'
import { storedServer, storedSession } from '../api/client'
import type { Mission } from '../api/types'
import { Modal } from './Modal'

/**
 * How teams get into this event.
 *
 * The code and the QR are the same credential in two forms, because the right one depends on where
 * everybody is standing: a code carries over a radio and across a car park, a QR is scanned once at
 * the safety briefing and cannot be misheard.
 */
export function JoinPanel({ mission }: { mission: Mission }) {
  const [showQr, setShowQr] = useState(false)
  const [copied, setCopied] = useState(false)

  if (!mission.joinCode) return null

  async function copy() {
    try {
      await navigator.clipboard.writeText(mission.joinCode!)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access is refused in some browsers without a user gesture chain, and the code is
      // on screen to read anyway, so this is not worth an error.
    }
  }

  return (
    <div>
      <div className="spread">
        <code style={{ fontSize: 22, letterSpacing: 3, color: 'var(--accent)' }}>
          {mission.joinCode}
        </code>
        <div className="row" style={{ gap: 6 }}>
          <button className="secondary" style={{ height: 28 }} onClick={copy}>
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button className="secondary" style={{ height: 28 }} onClick={() => setShowQr(true)}>
            QR
          </button>
        </div>
      </div>

      <p className="tertiary" style={{ margin: '8px 0 0' }}>
        A team admin enters this to bring their squad in. Keep it to the teams you invited.
      </p>

      {showQr && (
        <Modal title="Scan to join" onClose={() => setShowQr(false)}>
          <div className="stack" style={{ alignItems: 'center' }}>
            <QrImage missionId={mission.id} />
            <code style={{ fontSize: 20, letterSpacing: 3 }}>{mission.joinCode}</code>
            <p className="tertiary" style={{ textAlign: 'center', margin: 0 }}>
              Show this at the briefing, or print it on the sign-in sheet.
            </p>
          </div>
        </Modal>
      )}
    </div>
  )
}

/**
 * The QR, fetched with the session token.
 *
 * An <img src> cannot carry an Authorization header, and the endpoint is deliberately protected —
 * so the bytes are fetched and turned into a blob URL instead.
 */
function QrImage({ missionId }: { missionId: string }) {
  const [src, setSrc] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false

    void (async () => {
      const session = storedSession()
      if (!session) return setFailed(true)

      try {
        const response = await fetch(
          `${storedServer()}/api/v1/missions/${missionId}/join-code/qr?size=512`,
          { headers: { Authorization: `Bearer ${session.accessToken}` } },
        )
        if (!response.ok) return setFailed(true)

        objectUrl = URL.createObjectURL(await response.blob())
        if (cancelled) URL.revokeObjectURL(objectUrl)
        else setSrc(objectUrl)
      } catch {
        setFailed(true)
      }
    })()

    // The blob is released when the dialog closes; leaking one per open would hold the image
    // bytes for the life of the tab, and a TOC stays open all day.
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [missionId])

  if (failed) return <p className="tertiary">Could not load the QR code.</p>
  if (!src) return <p className="tertiary">Rendering…</p>

  return (
    <img
      src={src}
      alt="QR code for joining this event"
      width={256}
      height={256}
      style={{ borderRadius: 8, background: '#fff', padding: 8 }}
    />
  )
}
